import React, { useState } from "react";
import "./HierarchyViewer.css";

// Zentrale, dynamische Hierarchie-Konfiguration.
// Reihenfolge = Stufe (oben = 1.Stufe). Jede Stufe hat eine eigene Farbe.
// "key" entspricht exakt dem Rollennamen aus der Datenbank (klein geschrieben).
// Labels sind ueber dieses Objekt zentral pflegbar (i18n-faehig: spaeter aus
// Resource-Keys befuellbar, ohne JSX anzufassen).
export const HIERARCHY_LEVELS = [
  { key: "admin", label: "Admin", color: "#6f42c1" },
  { key: "mainvo", label: "Haupt-VO", color: "#0d6efd" },
  { key: "subvo", label: "Reseller (Sub-VO)", color: "#20c997" },
  { key: "adm", label: "ADM", color: "#fd7e14" },
  { key: "customer", label: "Kunde", color: "#dc3545" },
  { key: "employee", label: "Mitarbeiter", color: "#198754" },
];

// Liefert zu einem Rollennamen die Hierarchie-Stufe (1-basiert) inkl. Farbe und
// Label. Gibt null zurueck, wenn die Rolle nicht Teil der Hierarchie ist.
// Wird sowohl vom Viewer als auch von der Benutzertabelle (Spalte "Stufe") genutzt.
export const getHierarchyLevel = (roleName) => {
  const normalized = (roleName || "").toString().trim().toLowerCase();
  const idx = HIERARCHY_LEVELS.findIndex((level) => level.key === normalized);
  if (idx === -1) return null;
  return { stufe: idx + 1, ...HIERARCHY_LEVELS[idx] };
};

// Wandelt eine HEX-Farbe in eine rgba-Zeichenkette mit Transparenz um.
// Wird fuer dezente Tints/Schatten genutzt (kein Hardcoding einzelner Farben).
const hexToRgba = (hex, alpha) => {
  const value = (hex || "").replace("#", "");
  if (value.length !== 6) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const HierarchyViewer = ({ activeRole, title = "Hierarchie-Viewer", defaultOpen = true }) => {
  // Panel rechts fixiert; per Klick auf-/zuklappbar.
  const [open, setOpen] = useState(!!defaultOpen);

  const normalized = (activeRole || "").toString().trim().toLowerCase();
  const activeIndex = HIERARCHY_LEVELS.findIndex(
    (level) => level.key === normalized
  );
  // Sobald eine Rolle markiert ist, werden alle uebrigen Rollen ausgegraut,
  // damit die markierte Stufe visuell klar hervorsticht.
  const hasSelection = activeIndex !== -1;

  const toggle = () => setOpen((prev) => !prev);

  return (
    <div className={`hv-dock${open ? " hv-dock--open" : " hv-dock--closed"}`}>
      {/* Geschlossen: schmaler Tab am rechten Rand zum Oeffnen */}
      {!open && (
        <button
          type="button"
          className="hv-tab"
          onClick={toggle}
          aria-expanded="false"
          aria-controls="hv-panel"
          title={`${title} öffnen`}
        >
          <span className="hv-tab-label">{title}</span>
        </button>
      )}

      {/* Geoeffnet: fixiertes Panel mit Schliessen-Button */}
      {open && (
        <aside
          id="hv-panel"
          className={`hv-panel${hasSelection ? " hv-panel--has-selection" : ""}`}
          aria-label={title}
        >
          <div className="hv-header">
            <h2 className="hv-title">{title}</h2>
            <button
              type="button"
              className="hv-toggle"
              onClick={toggle}
              aria-expanded="true"
              aria-controls="hv-panel"
              title={`${title} schließen`}
              aria-label={`${title} schließen`}
            >
              ×
            </button>
          </div>

          <ol className="hv-list" role="list">
            {HIERARCHY_LEVELS.map((level, idx) => {
              const isActive = idx === activeIndex;
              const isDimmed = hasSelection && !isActive;
              return (
                <li
                  key={level.key}
                  className={`hv-item${isActive ? " hv-item--active" : ""}${isDimmed ? " hv-item--dimmed" : ""}`}
                  aria-current={isActive ? "true" : undefined}
                >
                  <span className="hv-stufe">{idx + 1}.Stufe</span>
                  <span
                    className="hv-bar"
                    style={{
                      backgroundColor: isActive ? level.color : hexToRgba(level.color, 0.12),
                      borderColor: level.color,
                      boxShadow: isActive ? `0 0 0 3px ${hexToRgba(level.color, 0.35)}` : "none",
                    }}
                  >
                    <span
                      className="hv-label"
                      style={{ color: isActive ? "#ffffff" : level.color }}
                    >
                      {level.label}
                    </span>
                  </span>
                </li>
              );
            })}
          </ol>

          {activeIndex === -1 && (
            <p className="hv-hint">
              Wählen Sie eine Zeile in der Tabelle aus, um die Position in der
              Hierarchie anzuzeigen.
            </p>
          )}
        </aside>
      )}
    </div>
  );
};

export default HierarchyViewer;
